import { useState } from 'react';
import { Link } from 'react-router-dom';

// Teacher external link share modal
function ShareLinkModal({ onClose }: { onClose: () => void }) {
  const [link, setLink] = useState('https://study.xiaoe-tech.com/');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleOpen = () => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md rounded-3xl overflow-hidden shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-lg">
              <i className="ri-share-box-line text-blue-600"></i>
            </div>
            <h2 className="font-black text-gray-900">分享课程链接</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 cursor-pointer transition-colors">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>
        <div className="p-6 flex flex-col gap-5">
          {/* Xiaoe-tech quick connect */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center bg-blue-600 rounded-xl flex-shrink-0">
                <i className="ri-video-chat-line text-white text-lg"></i>
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">小鹅通课程平台</p>
                <p className="text-xs text-gray-500">study.xiaoe-tech.com</p>
              </div>
              <button
                onClick={handleOpen}
                className="ml-auto px-4 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-full hover:bg-blue-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                前往平台
              </button>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              如果您已在小鹅通上传了课程，可以直接分享您的课程链接给学生，学生点击即可跳转购买。
            </p>
          </div>

          {/* Custom link input */}
          <div>
            <label className="text-xs font-bold text-gray-600 mb-2 block">您的课程链接</label>
            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 gap-2 focus-within:border-blue-400 transition-colors">
              <i className="ri-links-line text-gray-400 text-sm flex-shrink-0"></i>
              <input
                type="url"
                value={link}
                onChange={e => setLink(e.target.value)}
                placeholder="https://study.xiaoe-tech.com/your-course"
                className="flex-1 bg-transparent text-sm outline-none min-w-0"
              />
            </div>
          </div>

          {/* Share actions */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleCopy}
              className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer border-2 ${
                copied
                  ? 'bg-green-50 border-green-400 text-green-600'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className={copied ? 'ri-checkbox-circle-line' : 'ri-file-copy-line'}></i>
              {copied ? '已复制！' : '复制链接'}
            </button>
            <button
              onClick={handleOpen}
              className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold bg-blue-600 text-white hover:bg-blue-500 transition-all cursor-pointer border-2 border-blue-600"
            >
              <i className="ri-external-link-line"></i>
              打开链接
            </button>
          </div>

          {/* Share via */}
          <div>
            <p className="text-xs font-bold text-gray-500 mb-2">分享到</p>
            <div className="flex gap-2">
              {[
                { icon: 'ri-wechat-fill', label: '微信', color: 'text-green-600 bg-green-50 hover:bg-green-100' },
                { icon: 'ri-weibo-fill', label: '微博', color: 'text-red-500 bg-red-50 hover:bg-red-100' },
                { icon: 'ri-qq-fill', label: 'QQ', color: 'text-blue-500 bg-blue-50 hover:bg-blue-100' },
              ].map(s => (
                <button key={s.label} className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${s.color}`}>
                  <i className={`${s.icon} text-base`}></i>{s.label}
                </button>
              ))}
            </div>
          </div>

          <p className="text-center text-xs text-gray-400">
            <i className="ri-information-line mr-1"></i>
            学生点击链接后可直接跳转到您的课程页面购买
          </p>
        </div>
      </div>
    </div>
  );
}
import { featuredVideos } from '@/mocks/videos';

const gaokaoVideos = featuredVideos.filter((v) => v.category === '高考冲刺');
const kaoyenVideos = featuredVideos.filter((v) => v.category === '考研');

type Tab = 'gaokao' | 'kaoyan';

interface VideoCardProps {
  video: (typeof featuredVideos)[0];
}

function VideoCard({ video }: VideoCardProps) {
  return (
    <Link
      to={`/videos/${video.id}`}
      className="bg-white rounded-2xl overflow-hidden group cursor-pointer hover:scale-[1.02] transition-all block"
    >
      <div className="relative overflow-hidden h-44">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 flex items-center justify-center bg-white/90 rounded-full">
            <i className="ri-play-fill text-red-600 text-xl"></i>
          </div>
        </div>
        <div className="absolute top-3 right-3 bg-red-600 text-white text-xs px-2.5 py-1 rounded-full font-medium">
          {video.category}
        </div>
        <div className="absolute bottom-3 left-3 text-white text-xs flex items-center gap-1">
          <i className="ri-time-line"></i>
          {video.duration}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-bold text-gray-900 text-sm leading-snug mb-2 line-clamp-2">{video.title}</h3>
        <p className="text-gray-400 text-xs mb-3">{video.teacher} · {video.grade}</p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <i className="ri-star-fill text-amber-400 text-xs"></i>
            <span className="text-xs font-semibold text-gray-700">{video.rating}</span>
            <span className="text-xs text-gray-400">({video.students.toLocaleString()}人)</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-red-600 font-bold text-sm">¥{video.price}</span>
            <span className="text-gray-300 text-xs line-through">¥{video.originalPrice}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function VideoPreviewSection() {
  const [activeTab, setActiveTab] = useState<Tab>('gaokao');
  const [showShareModal, setShowShareModal] = useState(false);

  const displayVideos = activeTab === 'gaokao' ? gaokaoVideos : kaoyenVideos;

  return (
    <section className="py-20 bg-[#faf9f7]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-4">
          <div>
            <p className="text-red-500 text-xs font-semibold tracking-widest uppercase mb-1">一站式备考解决方案</p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">
              从学生到职场精英
            </h2>
            <p className="text-gray-500 mt-2 text-sm">精选名师课程，助力高考·考研冲刺</p>
          </div>
          <Link
            to="/videos"
            className="flex items-center gap-2 text-red-600 font-semibold text-sm hover:gap-3 transition-all cursor-pointer whitespace-nowrap"
          >
            查看全部课程 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-2 mb-8">
          <div className="inline-flex bg-gray-100 rounded-full px-1 py-1 gap-1">
            <button
              onClick={() => setActiveTab('gaokao')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'gaokao'
                  ? 'bg-red-600 text-white'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              <i className="ri-fire-line mr-1.5"></i>高考冲刺
            </button>
            <button
              onClick={() => setActiveTab('kaoyan')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'kaoyan'
                  ? 'bg-red-600 text-white'
                  : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              <i className="ri-medal-line mr-1.5"></i>考研精讲
            </button>
          </div>
          <span className="text-xs text-gray-400 ml-2">
            共 {displayVideos.length} 门课程
          </span>
        </div>

        {/* Teacher Connect Banner */}
        <div className="mb-8 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-blue-500/20 rounded-xl flex-shrink-0">
              <i className="ri-video-upload-line text-blue-400 text-xl"></i>
            </div>
            <div>
              <p className="text-white font-bold text-sm">教师专区 · 已有小鹅通课程？</p>
              <p className="text-white/50 text-xs mt-0.5">一键连接 study.xiaoe-tech.com，分享您的课程链接给学生</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <a
              href="https://study.xiaoe-tech.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap border border-white/20"
            >
              <i className="ri-external-link-line"></i>前往小鹅通
            </a>
            <button
              onClick={() => setShowShareModal(true)}
              className="flex items-center gap-2 px-5 py-2 bg-blue-500 hover:bg-blue-400 text-white text-sm font-bold rounded-full transition-all cursor-pointer whitespace-nowrap shadow-lg"
            >
              <i className="ri-share-forward-line"></i>分享课程链接
            </button>
          </div>
        </div>

        {/* Video Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {displayVideos.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 text-center">
          <Link
            to="/videos"
            className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-full text-sm font-semibold hover:bg-gray-700 transition-all cursor-pointer whitespace-nowrap"
          >
            查看全部 {activeTab === 'gaokao' ? '高考' : '考研'} 课程
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>

      {showShareModal && <ShareLinkModal onClose={() => setShowShareModal(false)} />}
    </section>
  );
}
